import React from "react";

type StyledCheckboxProps = {
  id: string;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label: React.ReactNode;
  name?: string;
  disabled?: boolean;
  className?: string;
};

/**
 * Themed replacement for the browser's default checkbox.
 *
 * The real <input> stays in the DOM (sr-only, not hidden) so the label
 * association, keyboard focus, and screen-reader semantics are the native
 * ones — the visible box is a sibling styled off Tailwind's `peer-*` states.
 * Colours come from the seasonal theme variables, so it follows the site.
 */
const StyledCheckbox = ({
  id,
  checked,
  onChange,
  label,
  name,
  disabled = false,
  className = "",
}: StyledCheckboxProps) => (
  <label
    htmlFor={id}
    className={`group inline-flex cursor-pointer select-none items-center gap-3 ${
      disabled ? "cursor-not-allowed opacity-60" : ""
    } ${className}`}
  >
    <input
      type="checkbox"
      id={id}
      name={name}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="peer sr-only"
    />
    <span
      aria-hidden="true"
      className="
        flex h-5 w-5 shrink-0 items-center justify-center rounded-md
        border-2 bg-[var(--color-cream-light)]
        border-[var(--color-wood)]
        transition-all duration-150 ease-out
        group-hover:border-[var(--color-navy)]
        peer-checked:border-[var(--color-navy)]
        peer-checked:bg-[var(--color-navy)]
        peer-focus-visible:ring-2
        peer-focus-visible:ring-[var(--color-accent)]
        peer-focus-visible:ring-offset-2
      "
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        className="h-3.5 w-3.5 transition-all duration-150 ease-out"
        style={{
          transform: checked ? "scale(1)" : "scale(0.5)",
          opacity: checked ? 1 : 0,
        }}
      >
        <path
          d="M3 8.5 6.5 12 13 4.5"
          stroke="var(--color-cream-light)"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
    <span className="text-[var(--color-text-default)]">{label}</span>
  </label>
);

export default StyledCheckbox;
